import { apiClient } from "../core/apiClient";
import { buildUrlWithParams } from "../core/utils/urlUtils";
import { fetchWithMockFallback } from "../core/utils/mockUtils";
import { USER_ENDPOINTS } from "./endpoints";
import {
  getMockUserDetails,
  getMockUserFollowers,
  getMockUserFollowing,
} from "./mockData";
import type { UserData } from "../../types";
import type {
  UserMeResponse,
  UserDetailsResponse,
  PaginatedContent,
  UserFollowersResponse,
  UserFollowingResponse,
} from "./interfaces";
import { ContentType, ConsumeType, Language, Theme } from "../../constants/enums";

/**
 * Получение данных текущего авторизованного пользователя
 * Требует авторизации
 *
 * @returns полные данные профиля пользователя, включая превью уведомлений
 */
export async function getCurrentUser(): Promise<UserData> {
  const response = await apiClient<UserMeResponse>(USER_ENDPOINTS.me, {
    method: "GET",
    requiresAuth: true,
  });

  return {
    id: response.id,
    username: response.username,
    email: response.email,
    imageUrl: response.imageUrl,
    themeId: response.themeId,
    languageId: response.languageId,
    notificationWidget: response.notificationWidget,
  };
}

/**
 * Получение публичного профиля пользователя
 * При ошибке возвращает mock данные
 *
 * @param userName - имя пользователя
 * @returns полные данные профиля пользователя
 */
export async function getUserDetails(
  userName: string,
): Promise<UserDetailsResponse> {
  return fetchWithMockFallback(
    () =>
      apiClient<UserDetailsResponse>(USER_ENDPOINTS.details(userName), {
        method: "GET",
      }),
    () => getMockUserDetails(userName),
    `user details for ${userName}`,
  );
}

/**
 * Параметры для получения consume контента
 */
export interface GetUserConsumeParams {
  contentType: ContentType;
  consumeType: ConsumeType;
  page?: number;
  pageSize?: number;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Получение consume контента пользователя с пагинацией
 *
 * @param userId - ID пользователя
 * @param params - параметры фильтрации и пагинации
 * @returns пагинированный список контента
 */
export async function getUserConsume(
  userId: string,
  params: GetUserConsumeParams,
): Promise<PaginatedContent> {
  const url = buildUrlWithParams(USER_ENDPOINTS.consume(userId), params);

  return apiClient<PaginatedContent>(url, {
    method: "GET",
  });
}

/**
 * Подписаться на пользователя
 * Требует авторизации
 *
 * @param userName - имя пользователя
 */
export async function followUser(userName: string): Promise<void> {
  return apiClient<void>(USER_ENDPOINTS.follow(userName), {
    method: "POST",
    requiresAuth: true,
  });
}

/**
 * Отписаться от пользователя
 * Требует авторизации
 *
 * @param userName - имя пользователя
 */
export async function unfollowUser(userName: string): Promise<void> {
  return apiClient<void>(USER_ENDPOINTS.unfollow(userName), {
    method: "DELETE",
    requiresAuth: true,
  });
}

/**
 * Параметры для получения списка подписчиков/подписок
 */
export interface GetUserFollowsParams {
  page?: number;
  pageSize?: number;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Получение списка подписчиков пользователя
 *
 * @param userName - имя пользователя
 * @param params - параметры пагинации
 * @returns пагинированный список подписчиков
 */
export async function getUserFollowers(
  userName: string,
  params: GetUserFollowsParams = {},
): Promise<UserFollowersResponse> {
  return fetchWithMockFallback(
    () => {
      const url = buildUrlWithParams(
        USER_ENDPOINTS.followers(userName),
        params,
      );
      return apiClient<UserFollowersResponse>(url, {
        method: "GET",
      });
    },
    () => getMockUserFollowers(userName),
    `user followers for ${userName}`,
  );
}

/**
 * Получение списка подписок пользователя
 *
 * @param userName - имя пользователя
 * @param params - параметры пагинации
 * @returns пагинированный список подписок
 */
export async function getUserFollowing(
  userName: string,
  params: GetUserFollowsParams = {},
): Promise<UserFollowingResponse> {
  return fetchWithMockFallback(
    () => {
      const url = buildUrlWithParams(
        USER_ENDPOINTS.following(userName),
        params,
      );
      return apiClient<UserFollowingResponse>(url, {
        method: "GET",
      });
    },
    () => getMockUserFollowing(userName),
    `user following for ${userName}`,
  );
}

/**
 * Обновить язык пользователя
 * Требует авторизации
 * 
 * ⚠️ TODO: Реализовать на бэкенде
 *
 * @param language - новый язык пользователя
 */
export async function updateUserLanguage(language: Language): Promise<void> {
  console.warn("⚠️ updateUserLanguage API is not implemented yet. Request would be:", {
    endpoint: USER_ENDPOINTS.updateLanguage,
    method: "PATCH",
    body: { languageId: language },
  });
  
  // Имитируем успешный запрос
  return Promise.resolve();
  
  // Когда бэкенд будет готов, раскомментируйте:
  // return apiClient<void>(USER_ENDPOINTS.updateLanguage, {
  //   method: "PATCH",
  //   requiresAuth: true,
  //   body: JSON.stringify({ languageId: language }),
  // });
}

/**
 * Обновить тему пользователя
 * Требует авторизации
 * 
 * ⚠️ TODO: Реализовать на бэкенде
 *
 * @param theme - новая тема пользователя
 */
export async function updateUserTheme(theme: Theme): Promise<void> {
  console.warn("⚠️ updateUserTheme API is not implemented yet. Request would be:", {
    endpoint: USER_ENDPOINTS.updateTheme,
    method: "PATCH",
    body: { themeId: theme },
  });
  
  // Имитируем успешный запрос
  return Promise.resolve();
  
  // Когда бэкенд будет готов, раскомментируйте:
  // return apiClient<void>(USER_ENDPOINTS.updateTheme, {
  //   method: "PATCH",
  //   requiresAuth: true,
  //   body: JSON.stringify({ themeId: theme }),
  // });
}
